'use client'

import { faAngleDown, faClose } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavigationMenu } from 'radix-ui'
import {
  type AbstractIntlMessages,
  useMessages,
  useTranslations,
} from 'next-intl'
import NextLink from 'next/link'
import { usePathname } from 'next/navigation'
import { type ReactNode } from 'react'
import { cn } from 'utils/cn'
import { Indicator } from './Indicator'
import { type MenuItem } from './typings'
import { isSamePath } from './utils'

export const MegaMenu = ({
  onMenuItemClick,
}: {
  onMenuItemClick?: (args: {
    clickedElement?: string
    clickUrl?: string
  }) => void
}) => {
  const t = useTranslations('Navigation.MegaMenu')

  return (
    <NavigationMenu.Root
      aria-label={t('ariaLabel')}
      className={cn('font-manrope text-body-md-bold relative')}
    >
      <NavigationMenu.List>
        <NavigationMenu.Item>
          <MegaMenuTrigger />
          <MegaMenuContent onMenuItemClick={onMenuItemClick} />
        </NavigationMenu.Item>
      </NavigationMenu.List>
      <Indicator />
      <div className={cn('absolute right-0 top-full flex justify-center')}>
        <NavigationMenu.Viewport className='bg-surface-primary @container/viewport data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut shadow-menu relative mt-[10px] h-[var(--radix-navigation-menu-viewport-height)] w-[var(--radix-navigation-menu-viewport-width)] min-w-min origin-[top_right] overflow-x-hidden rounded-lg transition-[width,height] duration-300' />
      </div>
    </NavigationMenu.Root>
  )
}

export const MegaMenuTrigger = () => {
  const t = useTranslations('Navigation.MegaMenu.MegaMenuTrigger')

  return (
    <NavigationMenu.Trigger
      className={cn(
        `border-text-400 data-[state=open]:border-primary-700 data-[state=open]:text-primary-700 hover:border-primary-700 hover:text-primary-700 active:border-primary-800 active:text-primary-800 group flex items-center gap-2 rounded-lg border px-5 py-3`,
      )}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <span className='group-data-[state=closed]:hidden'>{t('open')}</span>
      <span className='group-data-[state=open]:hidden'>{t('closed')}</span>
      <FontAwesomeIcon
        className='block group-data-[state=open]:hidden'
        icon={faAngleDown}
      />
      <FontAwesomeIcon
        className='block group-data-[state=closed]:hidden'
        icon={faClose}
      />
    </NavigationMenu.Trigger>
  )
}

export const MegaMenuContent = ({
  onMenuItemClick,
}: {
  onMenuItemClick?: (args: {
    clickedElement?: string
    clickUrl?: string
  }) => void
}) => {
  const messages = useMessages() as AbstractIntlMessages & {
    Navigation: {
      MegaMenu: {
        Items: Record<string, MenuItem>
      }
    }
  }
  const items = messages.Navigation.MegaMenu.Items

  return (
    <NavigationMenu.Content
      className={cn(
        `text-text-800 font-manrope text-body-md flex w-[calc(100vw-theme(spacing.20))] min-w-min max-w-[calc(theme(screens.content)-theme(spacing.8))] flex-col gap-4 rounded-lg p-10 pb-0 lg:w-[calc(100vw-theme(spacing.28))]`,
      )}
    >
      <ul className='flex justify-between gap-6'>
        {Object.entries(items).map(([_, item], i) => (
          <MenuSection key={i} title={item.title}>
            {Object.entries(item.Children ?? {}).map(([__, childItem], j) => (
              <MenuItem
                key={j}
                onMenuItemClick={onMenuItemClick}
                path={childItem.path}
                title={childItem.title}
              />
            ))}
          </MenuSection>
        ))}
      </ul>
      <div className='text-body-xs self-end pb-4'>Inspires to book</div>
    </NavigationMenu.Content>
  )
}

const MenuSection = ({
  children,
  title,
}: {
  children: ReactNode
  title: string
}) => (
  <li className='flex flex-col gap-6'>
    <div className='text-body-lg-bold'>{title}</div>
    <ul className='flex flex-col gap-2'>{children}</ul>
  </li>
)

const MenuItem = ({
  onMenuItemClick,
  path,
  title,
}: {
  onMenuItemClick?: (args: {
    clickedElement?: string
    clickUrl?: string
  }) => void
  path: string
  title: string
}) => {
  return (
    <li>
      <NavigationMenu.Link
        asChild
        className={cn(
          `hover:bg-surface-tealgrey-50 focus:bg-surface-tealgrey-50 active:bg-surface-tealgrey-200 -mx-3 block rounded-lg px-3 py-2`,
          {
            'text-primary-700 font-bold': isSamePath(usePathname(), path),
          },
        )}
      >
        <NextLink
          href={path}
          onClick={() => {
            onMenuItemClick?.({
              clickedElement: title,
              clickUrl: path,
            })
          }}
          prefetch={false}
        >
          {title}
        </NextLink>
      </NavigationMenu.Link>
    </li>
  )
}
